import { useMemo, useState } from "react";
import type { ExtractedData, Fingerprint, SuggestedTemplate } from "../api";
import { createType } from "../api";
import { TemplateEditor } from "./TemplateEditor";

type Props = {
  fingerprint: Fingerprint;
  suggestion: SuggestedTemplate;
  /** Door Gemini al geëxtraheerd — gebruiken voor auto-naam + voorbeeldwaarden. */
  extracted: ExtractedData | null | undefined;
  onCreated: () => void;
};

/**
 * Klant-vriendelijke "nieuw document-type opslaan"-flow.
 *
 * In plaats van rauwe TemplateEditor met regex-patronen + strategieën die
 * eindgebruikers afschrikken, tonen we:
 * - Auto-ingevulde naam + afzender (uit Gemini-extractie)
 * - Checkbox-lijst van gevonden velden mét concrete voorbeeldwaarden
 *   ("Factuurnummer: 20200396 ✓")
 * - Prominente opslaan-knop in actie-taal
 * - Technische regex/strategie-editor onder `<details>` voor power-users
 *
 * De bestaande TemplateEditor blijft werken en is zichtbaar bij "Geavanceerd";
 * de default view toont hem niet.
 */
export function FriendlyTypeConfirm({
  fingerprint,
  suggestion,
  extracted,
  onCreated,
}: Props) {
  // Auto-vulling uit Gemini-extractie
  const velden = (extracted?.velden ?? {}) as Record<string, unknown>;

  const autoAfzender = useMemo(() => {
    // Probeer seller.name eerst (nested), dan platte name-alias
    const seller = velden.seller;
    if (seller && typeof seller === "object" && "name" in seller) {
      return String((seller as Record<string, unknown>).name ?? "");
    }
    if (typeof velden.name === "string") return velden.name;
    return "";
  }, [velden]);

  const autoCategorie = useMemo(() => {
    // Afleiden uit welke velden Gemini vond
    const veldNamen = new Set(suggestion.velden.map((v) => v.veld));
    if (veldNamen.has("invoice_number") || veldNamen.has("subtotal")) return "factuur";
    if (veldNamen.has("cmr_number")) return "cmr";
    if (veldNamen.has("bl_number")) return "bill of lading";
    if (veldNamen.has("awb_number")) return "luchtvrachtbrief";
    if (veldNamen.has("despatch_advice_number")) return "pakbon";
    if (veldNamen.has("packing_list_number")) return "paklijst";
    return "document";
  }, [suggestion]);

  const autoNaam = autoAfzender ? `${autoAfzender} ${autoCategorie}` : "";

  const [naam, setNaam] = useState(autoNaam);
  const [afzender, setAfzender] = useState(autoAfzender);
  const [categorie, setCategorie] = useState(autoCategorie);
  const [aanVelden, setAanVelden] = useState<Set<string>>(
    () => new Set(suggestion.velden.map((v) => v.veld))
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookupVoorbeeld = (veld_naam: string): string => {
    const parts = veld_naam.split(".");
    let cur: unknown = velden;
    for (const p of parts) {
      if (cur && typeof cur === "object") {
        cur = (cur as Record<string, unknown>)[p];
      } else {
        return "";
      }
    }
    return typeof cur === "string" ? cur : "";
  };

  const toggle = (v: string) => {
    const next = new Set(aanVelden);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setAanVelden(next);
  };

  const doOpslaan = async (overrideVelden?: unknown[], overrideTabellen?: unknown[]) => {
    const effectieveNaam = naam.trim() || autoNaam || "Onbenoemd document";
    setSaving(true);
    setError(null);
    try {
      const filtered = overrideVelden
        ?? suggestion.velden.filter((v) => aanVelden.has(v.veld));
      await createType({
        naam: effectieveNaam,
        afzender: afzender.trim() || null,
        taal: "nl",
        categorie: categorie.trim() || null,
        fingerprint,
        template: {
          versie: suggestion.versie,
          velden: filtered,
          tabellen: overrideTabellen ?? suggestion.tabellen,
          // validaties worden server-side afgeleid uit velden+tabellen
        } as Record<string, unknown>,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Context-banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="font-medium text-blue-900">Nog niet eerder gezien</div>
        <p className="text-sm text-blue-800 mt-1">
          Dit document is nieuw. We hebben de velden al automatisch geëxtraheerd —
          controleer of de instellingen hieronder kloppen en Forms herkent dit type
          voortaan automatisch zonder tussenkomst.
        </p>
      </div>

      {/* Afzender + naam — alleen de 2 dingen die klant hoeft te bevestigen */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Afzender van dit document
          </label>
          <input
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={afzender}
            onChange={(e) => {
              const v = e.target.value;
              setAfzender(v);
              // Auto-naam volgt afzender tot de user 'em zelf aanpast
              if (!naam || naam === autoNaam) {
                setNaam(v ? `${v} ${categorie}` : "");
              }
            }}
            placeholder="bv. Cooperatie Sevesto U.A."
          />
          {autoAfzender && (
            <p className="text-xs text-gray-500 mt-1">
              ✓ Automatisch gelezen uit het document — pas aan als dit niet klopt
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Hoe wil je dit document-type noemen?
          </label>
          <input
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            placeholder={autoNaam || "bv. Sevesto factuur"}
          />
        </div>
      </div>

      {/* Velden-overzicht met voorbeeldwaarden */}
      <div className="bg-white border rounded-lg">
        <div className="px-4 py-3 border-b">
          <h3 className="font-medium text-gray-900">
            Welke informatie wil je automatisch uitlezen?
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Vink velden uit die je niet nodig hebt. {aanVelden.size} van{" "}
            {suggestion.velden.length} aangevinkt.
          </p>
        </div>
        <ul className="divide-y max-h-96 overflow-y-auto">
          {suggestion.velden.map((v) => {
            const voorbeeld = lookupVoorbeeld(v.veld);
            const vriendelijkeNaam = v.label || v.veld.replace(/_/g, " ");
            const aan = aanVelden.has(v.veld);
            return (
              <li key={v.veld} className="px-4 py-2.5 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={aan}
                  onChange={() => toggle(v.veld)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate capitalize">
                    {vriendelijkeNaam.replace(/:$/, "")}
                  </div>
                  {voorbeeld ? (
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      Voorbeeld uit dit document: <span className="font-medium text-gray-700">{voorbeeld}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mt-0.5">
                      Geen voorbeeldwaarde beschikbaar
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Prominente actie-knop */}
      <div className="flex items-center gap-4 pt-2">
        <button
          type="button"
          onClick={() => doOpslaan()}
          disabled={saving}
          className="bg-blue-600 text-white px-5 py-2.5 rounded font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? "Bezig met opslaan…" : "Opslaan — volgende keer automatisch herkennen"}
        </button>
        <button
          type="button"
          onClick={() => onCreated()}
          disabled={saving}
          className="text-sm text-gray-600 hover:underline"
        >
          Annuleren
        </button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Geavanceerd — regex, strategie, categorie-override */}
      <div className="bg-white border rounded-lg">
        <button
          type="button"
          onClick={() => setShowAdvanced((s) => !s)}
          className="w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-between"
        >
          <span>
            {showAdvanced ? "▾" : "▸"} Geavanceerd: categorie, regex-patronen, strategie
          </span>
          <span className="text-xs text-gray-400">voor power-users</span>
        </button>
        {showAdvanced && (
          <div className="p-4 border-t space-y-4">
            <div>
              <label className="block text-sm font-medium">Categorie (voor filteren)</label>
              <input
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Taal staat vast op 'nl'. Overige types ondersteund in schemas_catalog.py.
              </p>
            </div>
            <TemplateEditor
              suggestion={suggestion}
              onCancel={() => onCreated()}
              onSubmit={async (template) => {
                await doOpslaan(
                  template.velden as unknown[],
                  template.tabellen as unknown[]
                );
              }}
              submitting={saving}
              submitLabel="Opslaan met handmatig aangepaste regex"
            />
          </div>
        )}
      </div>
    </div>
  );
}
